import type { Router } from 'found';
import type { TFunction } from 'i18next';

import DownloadIcon from '../../../assets/downloadBanner.svg';
import ReachedLimitIcon from '../../../assets/reachedLimit.svg';
import { externalLinks, routes } from '../../../constants';
import type { User } from '../../../types/User';
import type { IconName } from '../../../utils/getMuiIcon';

export interface UpgradeBannerProps {
	Image: React.ElementType;
	title: string;
	subtitle?: string;
	button: {
		text: string;
		onClick: () => void;
		icon: IconName;
	};
}

export const getDownloadBannerProps = (
	user: User,
	downloadMindItem: () => void,
	router: Router,
	t: TFunction
) => {
	const { isCourseUser, isLibraryUser, isSchoolUser, downloads } = user;
	const downloadsRemaining = downloads.max - downloads.counter;
	const isLibraryOrSchoolCourseUser = isLibraryUser || (isSchoolUser && isCourseUser);

	if (downloadsRemaining > 0) {
		return {
			Image: DownloadIcon,
			title: t('Document download'),
			subtitle: t(
				'You have {{downloadsRemaining}} free downloads left. ' +
					'Use them to download materials for your lessons.',
				{ downloadsRemaining }
			),
			button: {
				text: t('Download'),
				onClick: () => downloadMindItem?.(),
				icon: 'downloadDocumentIcon' as const,
			},
		};
	}
	const subtitle = isLibraryOrSchoolCourseUser
		? 'You have reached the download limit of your plan. Learn more about how to download unlimited materials.'
		: 'You have reached the download limit of your plan. Adjust your plan now to download unlimited materials.';

	const button = isLibraryOrSchoolCourseUser
		? {
				text: t('Learn more'),
				onClick: () => window.open(externalLinks.schoolLicenceOffer, '_blank'),
				icon: 'starBanner' as const,
			}
		: {
				text: t('Adjust plan'),
				onClick: () => router.push(`/${routes.upgradePackageSelection.root}`),
				icon: 'starBanner' as const,
			};

	return {
		Image: ReachedLimitIcon,
		title: t('Download limit exceeded'),
		subtitle: t(subtitle),
		button,
	};
};
